import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// External components
import { Box, Grid, Button, Typography, useTheme } from "@mui/material";
// Graphql
import { useQuery } from "@apollo/client";
import { GET_NOTES_FOR_REVIEW } from "../../graphql/note/note.queries";
// Components
import { CreditDebitCard } from "../../components";
import LandingPage from "../LandingPage";
// Helpers
import { useModal } from "../../context";
import ElectronicBillDetail from "../../components/ElectronicBillDetail/ElectronicBillDetail";
import { ELECTRONIC_BILLS_DOCUMENT_TYPES } from "../../Enums/ElectronicBill";

function DebitNotes() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState({ creditNotes: [], debitNotes: [] });
  const { id } = useParams();
  const { data: notesData, loading: isLoadingNotes } = useQuery(
    GET_NOTES_FOR_REVIEW,
    {
      variables: {
        NotesForReviewInput: {
          where: {
            FK_ReferencedElectronicBill: Number(id),
          },
        },
      },
      fetchPolicy: "no-cache",
      context: {
        clientName: "apiEB",
      },
    },
  );
  const { setModalOpen } = useModal();
  const theme = useTheme();
  const isDarkModeEnabled = theme.palette.mode === "dark";

  function handleNoteClick(note) {
    setModalOpen(
      true,
      <Box
        sx={{
          width: "900px",
          padding: "32px",
          backgroundColor: isDarkModeEnabled ? "#030710" : "white",
        }}
      >
        <ElectronicBillDetail electronicDocument={note} />
      </Box>,
      "",
      false,
      true,
    );
  }

  const getLatestNoteByDate = notesArray =>
    notesArray.reduce((latest, current) =>
      new Date(current.Created_At) > new Date(latest.Created_At)
        ? current
        : latest,
    );

  const getBaseEBToCreateNewNote = () => {
    const { creditNotes, debitNotes } = notes;
    if (creditNotes.length > 0 || debitNotes.length > 0)
      return getLatestNoteByDate([...creditNotes, ...debitNotes]);
    return { id };
  };

  useEffect(() => {
    if (isLoadingNotes) return;
    if (!notesData?.getNotesForReview) return;

    const notesByType = notesData.getNotesForReview.reduce(
      (acc, note) => {
        const { NoteDetails, ...rest } = note;
        const transformedNote = {
          ...rest,
          details: NoteDetails,
        };
        if (
          note.ElectronicBillDocumentType.id ===
          ELECTRONIC_BILLS_DOCUMENT_TYPES.CREDIT_NOTE.toString()
        ) {
          acc.creditNotes.push(transformedNote);
        } else if (
          note.ElectronicBillDocumentType.id ===
          ELECTRONIC_BILLS_DOCUMENT_TYPES.DEBIT_NOTE.toString()
        ) {
          acc.debitNotes.push(transformedNote);
        }
        return acc;
      },
      { creditNotes: [], debitNotes: [] },
    );

    setNotes(notesByType);
  }, [isLoadingNotes, notesData]);

  return (
    <Box className="content-flex-column">
      <Typography
        align="center"
        fontWeight={600}
        typography="modeColor"
        variant="h6"
        width="100%"
      >
        Notas de crédito y debito
      </Typography>
      {isLoadingNotes && <LandingPage />}
      <br />

      <Grid
        container
        align="center"
        justifyContent="center"
        marginX="auto"
        maxWidth={1570}
        spacing={2}
      >
        <Grid item lg={4} md={5} sm={6} xl={3} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                align="center"
                typography="modeColor"
                variant="h5"
                width="100%"
              >
                Notas de crédito
                {` ${notes.creditNotes.length || 0}`}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {!isLoadingNotes && (
                <Button
                  className="btn-animate"
                  color="secondary"
                  size="small"
                  variant="contained"
                  onClick={() =>
                    navigate(
                      `/electronic-bills/${getBaseEBToCreateNewNote().id}/CreditNote`,
                    )
                  }
                >
                  Nueva nota de crédito
                </Button>
              )}
            </Grid>
            <Grid item sm={12} />
            <Grid item sm={12} />
            {notes.creditNotes.length > 0 && (
              <Grid container spacing={1}>
                {notes.creditNotes.map(note => (
                  <Grid key={note.id} item sx={{ marginBottom: 1 }} xs={12}>
                    <CreditDebitCard
                      note={note}
                      onClick={() => handleNoteClick(note)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item lg={4} md={5} sm={6} xl={3} xs={12}>
          <Grid container spacing={2} sx={{ marginBottom: 1 }}>
            <Grid item xs={12}>
              <Typography typography="modeColor" variant="h5" width="100%">
                Notas de débito {notes.debitNotes.length ?? "0"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {!isLoadingNotes && (
                <Button
                  className="btn-animate"
                  color="secondary"
                  size="small"
                  variant="contained"
                  onClick={() =>
                    navigate(
                      `/electronic-bills/${getBaseEBToCreateNewNote().id}/DebitNote`,
                    )
                  }
                >
                  Nueva nota de débito
                </Button>
              )}
            </Grid>
            <Grid item sm={12} />
            <Grid item sm={12} />
            {notes.debitNotes.length > 0 && (
              <Grid container spacing={1}>
                {notes.debitNotes.map(note => (
                  <Grid key={note.id} item sx={{ marginBottom: 1 }} xs={12}>
                    <CreditDebitCard
                      note={note}
                      onClick={() => handleNoteClick(note)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
DebitNotes.propTypes = {};
export default DebitNotes;
